"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PlanePoint,
} from "@/components/tools/math/coordinate-plane";
import {
  FormulaSteps,
  SquareSteps,
} from "@/components/tools/math/quadratic-steps";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng } from "@/lib/tool-kit/download";
import { samplePath } from "@/lib/tool-kit/math/plot";
import {
  formatFactored,
  formatQuadraticExpression,
  formatSurdPair,
  graphWindow,
  type QuadraticSolution,
  quadraticAt,
  solveLinear,
  solveQuadratic,
} from "@/lib/tool-kit/math/quadratic";
import {
  formatFraction,
  type NumberStyle,
  parseRational,
  RATIONAL_ZERO,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const QUADRATIC_EQUATION_SOLVER_STRINGS = {
  a: "a (coefficient of x²)",
  b: "b (coefficient of x)",
  c: "c (constant term)",
  coefficientHint: "A whole number, decimal or fraction such as 3/4.",
  results: "Results",
  roots: "Roots",
  discriminant: "Discriminant b² − 4ac",
  nature: "Type of roots",
  natures: {
    "two-rational": "Two rational roots",
    "two-irrational": "Two irrational roots",
    repeated: "One repeated root",
    complex: "Two complex roots",
  },
  vertex: "Vertex",
  axis: "Axis of symmetry",
  opensLine: "The parabola opens {direction}.",
  opensUp: "upward (a > 0)",
  opensDown: "downward (a < 0)",
  decimals: "As decimals",
  factoredLine: "Factored form: {form}",
  notFactorable:
    "The roots are not rational, so the quadratic has no factored form with whole-number coefficients.",
  vieta: "Check: x₁ + x₂ = −b/a = {sum} and x₁ × x₂ = c/a = {product}.",
  formulaHeading: "Solving with the quadratic formula",
  squareHeading: "Solving by completing the square",
  graphHeading: "Graph",
  graphTitle:
    "Graph of y = {equation}, with its vertex and any x-intercepts marked",
  point: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  copyRoots: "Copy roots",
  copied: "Copied!",
  rootsJoin: "{first} and {second}",
  stepRescale:
    "Multiply every term by {factor} so all the coefficients are whole numbers: {equation} = 0. The roots do not change.",
  stepIdentify: "Identify the coefficients: a = {a}, b = {b}, c = {c}.",
  stepDiscriminant: "Discriminant: Δ = b² − 4ac = {working} = {value}.",
  stepPerfectSquare:
    "Δ is positive and a perfect square ({root}²), so there are two rational roots.",
  stepPositive:
    "Δ is positive but not a perfect square, so there are two irrational roots.",
  stepZero: "Δ = 0, so there is exactly one (repeated) root.",
  stepNegative:
    "Δ is negative, so there are no real roots: the two roots are complex conjugates.",
  stepFormula: "Quadratic formula: x = (−b ± √Δ) / (2a) = {substituted}.",
  stepSimplifyRoot: "Simplify the square root: {root}.",
  stepRoots: "So {roots}.",
  stepRepeated: "x = −b / (2a) = {value}.",
  csDivide: "Divide every term by a = {a}: {equation} = 0.",
  csMove: "Move the constant term to the right: {left} = {right}.",
  csAdd:
    "Add (b/2a)² = ({half})² = {halfSquared} to both sides: {left} = {right}.",
  csSquare: "The left side is now a perfect square: {square} = {right}.",
  csRoot: "Take the square root of both sides: {shifted} = ±{root}.",
  csRootComplex:
    "The right side is negative, so its square roots are imaginary: {shifted} = ±{root}.",
  csZero: "The right side is 0, so {shifted} = 0.",
  csSolve: "Solve for x: x = {solution}.",
  csSolveRational:
    "Solve for x: x = {solution}, so x = {first} or x = {second}.",
  linear:
    "With a = 0 there is no x² term, so this is the linear equation {equation} = 0, and x = {root}.",
  linearNone:
    "With a = 0 and b = 0 the equation says {c} = 0, which is never true: there is no solution.",
  linearAll: "With a, b and c all 0, every value of x is a solution.",
  enterCoefficients: "Enter a, b and c to solve ax² + bx + c = 0.",
  invalid:
    "Enter each coefficient as a number or a fraction, such as 2, −0.5 or 3/4.",
} as const;

type Strings = typeof QUADRATIC_EQUATION_SOLVER_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const DECIMAL_INPUT = /[.,]/;

function short(fmt: Format, value: number): string {
  return fmt(value, { maximumFractionDigits: 4 });
}

function exactRoots(solution: QuadraticSolution): string {
  if (solution.rationalRoots) {
    return solution.rationalRoots.map(formatFraction).join(", ");
  }
  return solution.surd ? formatSurdPair(solution.surd) : "";
}

function decimalRoots(solution: QuadraticSolution, fmt: Format): string {
  if (solution.kind === "complex") {
    const { re, im } = solution.decimals[1];
    return `${short(fmt, re)} ± ${short(fmt, Math.abs(im))}i`;
  }
  return solution.decimals.map((d) => short(fmt, d.re)).join(", ");
}

function Graph({
  solution,
  strings,
  style,
}: {
  solution: QuadraticSolution;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const viewport = graphWindow(solution);
  const f = quadraticAt(solution);
  const label = (x: number, y: number) =>
    t(strings.point, { x: short(fmt, x), y: short(fmt, y) });
  const h = toNumberR(solution.vertex.x);
  const k = toNumberR(solution.vertex.y);
  const intercepts = solution.kind === "complex" ? [] : solution.decimals;
  const equation = formatQuadraticExpression(
    solution.a,
    solution.b,
    solution.c,
    style
  );
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          Math.abs(value) >= 1e5
            ? fmt(value, { notation: "compact", maximumFractionDigits: 1 })
            : fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={t(strings.graphTitle, { equation })}
        viewport={viewport}
      >
        {(scale) => (
          <>
            <path
              d={samplePath(f, viewport, scale.x, scale.y)}
              fill="none"
              stroke={CHART_COLORS[0]}
              strokeWidth={2.5}
            />
            {intercepts.map((root) => (
              <PlanePoint
                color={CHART_COLORS[3]}
                cx={scale.x(root.re)}
                cy={scale.y(0)}
                key={`root-${root.re}`}
                label={intercepts.length === 1 ? undefined : label(root.re, 0)}
              />
            ))}
            <PlanePoint
              below={solution.a.num > BigInt(0)}
              color={CHART_COLORS[2]}
              cx={scale.x(h)}
              cy={scale.y(k)}
              label={label(h, k)}
            />
          </>
        )}
      </CoordinatePlane>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("quadratic-graph.png", ref.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {strings.downloadPng}
      </button>
    </div>
  );
}

function Results({
  solution,
  strings,
  style,
}: {
  solution: QuadraticSolution;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const roots = exactRoots(solution);
  const vertex = t(strings.point, {
    x: formatFraction(solution.vertex.x),
    y: formatFraction(solution.vertex.y),
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.roots} value={roots} />
        <StatCard
          label={strings.decimals}
          value={decimalRoots(solution, fmt)}
        />
        <StatCard
          label={strings.discriminant}
          value={formatFraction(solution.discriminant)}
        />
        <StatCard
          label={strings.nature}
          value={strings.natures[solution.kind]}
        />
        <StatCard label={strings.vertex} value={vertex} />
        <StatCard
          label={strings.axis}
          value={`x = ${formatFraction(solution.vertex.x)}`}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyRoots}
        small
        text={`x = ${roots}`}
      />
      <div className={`${RESULT_CARD} space-y-2 text-sm`}>
        <p className="text-muted-foreground">
          {t(strings.opensLine, {
            direction:
              solution.a.num > BigInt(0) ? strings.opensUp : strings.opensDown,
          })}
        </p>
        {solution.factored ? (
          <p className="font-mono text-foreground">
            {t(strings.factoredLine, {
              form: formatFactored(solution.factored),
            })}
          </p>
        ) : (
          <p className="text-muted-foreground">{strings.notFactorable}</p>
        )}
        <p className="text-muted-foreground">
          {t(strings.vieta, {
            sum: formatFraction(solution.sum),
            product: formatFraction(solution.product),
          })}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      <Graph solution={solution} strings={strings} style={style} />
      <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
      <FormulaSteps solution={solution} strings={strings} />
      <h3 className={WIDGET_H3}>{strings.squareHeading}</h3>
      <SquareSteps solution={solution} strings={strings} />
    </>
  );
}

function LinearCase({
  b,
  c,
  strings,
  style,
}: {
  b: Rational;
  c: Rational;
  strings: Strings;
  style: NumberStyle;
}) {
  const solution = solveLinear(b, c);
  if (solution.kind === "all") {
    return <Notice>{strings.linearAll}</Notice>;
  }
  if (solution.kind === "none") {
    return <Notice>{t(strings.linearNone, { c: formatFraction(c) })}</Notice>;
  }
  return (
    <Notice>
      {t(strings.linear, {
        equation: formatQuadraticExpression(RATIONAL_ZERO, b, c, style),
        root: formatFraction(solution.root),
      })}
    </Notice>
  );
}

function Outcome({
  a,
  b,
  c,
  strings,
}: {
  a: string;
  b: string;
  c: string;
  strings: Strings;
}) {
  if ([a, b, c].some((text) => text.trim() === "")) {
    return <Notice>{strings.enterCoefficients}</Notice>;
  }
  const ra = parseRational(a);
  const rb = parseRational(b);
  const rc = parseRational(c);
  if (ra === null || rb === null || rc === null) {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  const style: NumberStyle = [a, b, c].some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const solution = solveQuadratic(ra, rb, rc);
  if (solution === null) {
    return <LinearCase b={rb} c={rc} strings={strings} style={style} />;
  }
  return <Results solution={solution} strings={strings} style={style} />;
}

export default function QuadraticEquationSolverWidget() {
  const strings = useToolUi(QUADRATIC_EQUATION_SOLVER_STRINGS);
  const [a, setA] = useState("2");
  const [b, setB] = useState("-3");
  const [c, setC] = useState("-5");
  const ra = parseRational(a);
  const rb = parseRational(b);
  const rc = parseRational(c);
  const preview =
    ra && rb && rc
      ? `${formatQuadraticExpression(ra, rb, rc, [a, b, c].some((text) => DECIMAL_INPUT.test(text)) ? "decimal" : "fraction")} = 0`
      : "ax² + bx + c = 0";
  return (
    <>
      <p className="rounded-lg border border-border bg-muted/30 p-4 text-center font-mono text-foreground text-lg">
        {preview}
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.coefficientHint}
          id="qe-a"
          label={strings.a}
          mono
          onChange={setA}
          value={a}
        />
        <TextField id="qe-b" label={strings.b} mono onChange={setB} value={b} />
        <TextField id="qe-c" label={strings.c} mono onChange={setC} value={c} />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome a={a} b={b} c={c} strings={strings} />
      </section>
    </>
  );
}
